import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Instagram, Music2, Send, MessageSquare, Mic2, ArrowRight } from 'lucide-react';
import { submitContactForm } from '../utils/api';
import { toast } from 'sonner';
import './ContactPage.css';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await submitContactForm(formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-overlay"></div>
        <div className="container contact-hero-content">
          <h1>Get In Touch</h1>
          <p>Have questions, want to collaborate, or just want to say hello? We'd love to hear from you.</p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h2>Contact Information</h2>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div className="method-details">
                    <h3>Email</h3>
                    <a href="mailto:info@anyonemusicrecords.com">info@anyonemusicrecords.com</a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <MessageSquare size={24} />
                  </div>
                  <div className="method-details">
                    <h3>Demo Submissions</h3>
                    <a href="mailto:demos@anyonemusicrecords.com">demos@anyonemusicrecords.com</a>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <MapPin size={24} />
                  </div>
                  <div className="method-details">
                    <h3>Location</h3>
                    <p>Toronto, Canada</p>
                    <p className="location-note">Global operations across 3 continents</p>
                  </div>
                </div>
              </div>

              <div className="social-section">
                <h3>Follow Us</h3>
                <div className="social-links">
                  <a href="https://instagram.com/anyonemusicrecords" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram">
                    <Instagram size={24} />
                  </a>
                  <a href="https://open.spotify.com/label/anyonemusicrecords" target="_blank" rel="noopener noreferrer" aria-label="Spotify" data-testid="social-spotify">
                    <Music2 size={24} />
                  </a>
                  <a href="https://soundcloud.com/anyonemusicrecords" target="_blank" rel="noopener noreferrer" aria-label="SoundCloud" data-testid="social-soundcloud">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.56 8.87V17h8.76c1.85 0 3.35-1.5 3.35-3.35s-1.5-3.35-3.35-3.35c-.37 0-.73.06-1.07.18-.49-2.42-2.64-4.25-5.18-4.25-1.33 0-2.55.49-3.51 1.28v10.49h1V8.87z"/>
                    </svg>
                  </a>
                </div>
              </div>

              <div className="response-info">
                <h3>Response Time</h3>
                <p>We typically respond within 24-48 hours. For demo submissions, please allow 4-6 weeks for review.</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              {submitted ? (
                <div className="form-success" data-testid="contact-success">
                  <div className="success-icon">
                    <Send size={48} />
                  </div>
                  <h2>Message Sent!</h2>
                  <p>Thank you for reaching out. We'll get back to you as soon as possible.</p>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', subject: '', message: '' });
                    }}
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2>Send a Message</h2>
                  <form onSubmit={handleSubmit} className="contact-form" data-testid="contact-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor="name">Your Name *</label>
                        <input
                          type="text"
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Enter your name"
                          required
                          data-testid="contact-name"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                          type="email"
                          id="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="Enter your email"
                          required
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subject">Subject *</label>
                      <select
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                        required
                        data-testid="contact-subject"
                      >
                        <option value="">Select a subject</option>
                        <option value="general">General Inquiry</option>
                        <option value="booking">Artist Booking</option>
                        <option value="press">Press & Media</option>
                        <option value="partnership">Partnership Opportunity</option>
                        <option value="demo">Demo Submission Question</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="message">Message *</label>
                      <textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us what's on your mind..."
                        rows="6"
                        required
                        data-testid="contact-message"
                      />
                    </div>

                    <button 
                      type="submit" 
                      className="btn btn-primary submit-btn"
                      disabled={submitting}
                      data-testid="contact-submit"
                    >
                      <Send size={18} />
                      {submitting ? 'Sending...' : 'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Demo CTA Section */}
      <section className="demo-cta-section">
        <div className="container">
          <div className="demo-cta-card">
            <Mic2 size={40} />
            <div className="demo-cta-text">
              <h3>Want to Join AMR?</h3>
              <p>Submit your demo and let us hear what you've got.</p>
            </div>
            <Link to="/submit-demo" className="btn btn-primary" data-testid="contact-demo-cta">
              <Send size={18} />
              Submit Demo
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          
          <div className="faq-grid">
            <div className="faq-item">
              <h3>How do I submit a demo?</h3>
              <p>Use our <Link to="/submit-demo">demo submission form</Link> to send us your tracks, or email demos@anyonemusicrecords.com with a brief bio and links to your socials.</p>
            </div>

            <div className="faq-item">
              <h3>What genres do you accept?</h3>
              <p>We specialize in high-energy electronic music including Deep House, Techno, Progressive House, and Organic House.</p>
            </div>

            <div className="faq-item">
              <h3>How long until I hear back?</h3>
              <p>We try our best to get back to you within two to three working days.</p>
            </div>

            <div className="faq-item">
              <h3>Do you book artists for events?</h3>
              <p>Yes! For booking inquiries, please select "Artist Booking" in the contact form above or email us directly.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
